// Text for the Forecast tab (and its label in SensorDetails). Kept with the component
// (component-local vue-i18n messages) while the forecast is a pilot; anything not found here
// falls back to the shared gw-js-lib locales.
export default {
  en: {
    sf: {
      tab: 'Forecast',
      measured: 'Measured (daily average)',
      forecast: 'Forecast (daily average)',
      range80: 'Likely range (80% chance)',
      range50: 'Most likely (50% chance)',
      ifNotIrrigated: 'If not irrigated',
      daily: 'Daily',
      hourly: 'Hourly',
      measuredHourly: 'Measured (hourly)',
      forecastHourly: 'Forecast (hourly)',
      hourlyNote: 'Hour by hour for the next 3 days. It starts from the latest reading and follows this sensor’s usual day-night pattern.',
      showHours: 'Show hours',
      forecastRegion: 'Forecast',
      horizon: { 3: 'Next 3 days', 7: 'Next 7 days', 14: 'Next 14 days' },
      skill: {
        3: 'Days 1–3 are the most reliable part of the forecast.',
        7: 'Days 4–7 are useful for planning; the range widens each day.',
        14: 'Days 8–14 show the trend only; they depend on rain forecasts that change often.',
      },
      noData: 'No forecast to show.',
      loading: 'Loading…',
      table: {
        day: 'Day',
        expected: 'Expected',
        range: 'Likely range',
        dry: 'If not irrigated',
        rain: 'Rain (chance)',
        et0: 'ET₀',
      },
      weatherTitle: 'Daily rain and evapotranspiration (ET₀)',
      rain: 'Rain',
      et0: 'ET₀',
      uncertaintyTitle: 'How sure is this forecast?',
      bandLive: 'The shaded range comes from {n} of this sensor’s own forecasts that were later checked against its readings.',
      bandHindcast: 'The shaded range comes from how far the model missed on this sensor over the last 30 days. It switches to checked forecasts once there are enough.',
      bandNone: 'No likely range is available for this reading yet; treat the line as a guide.',
      trackRecord: '3-day forecasts checked so far: {n}. Average miss {fc} pts, vs {ps} pts for “same as today”.',
      trackPending: 'No forecasts checked yet. Its track record appears here once the first forecasts come due (about 3 days).',
      outlookTitle: 'Outlook',
      mlTitle: 'Machine-learning forecast',
      mlExplain: 'Fitted to this sensor’s own readings and local weather on every run. Each forecast is later checked against what the sensor actually read, and those checks correct the next one.',
      updated: 'Updated {when}',
      tierLabel: 'Reliability',
      tier: {
        insufficient: 'Not enough data',
        learning: 'Learning',
        good: 'Good',
        mature: 'Mature',
      },
      tierScale: 'Learning from 21 days of clean readings · Good from 45 · Mature from 60.',
      cleanDays: '{n} days of clean readings in the last 90',
      tierNeed: '{days} more days of clean readings to reach “{tier}”.',
      tierMature: 'Enough history for the most reliable forecasts.',
      reasonTitle: 'No forecast for this sensor right now',
      reason: {
        no_data: 'The sensor has no recent readings.',
        stale: 'The sensor stopped reporting. Forecasts resume once readings come back.',
        out_of_ground: 'Readings look like the probe is out of the soil (moisture near zero, no salinity).',
        insufficient_history: 'Not enough history yet. Forecasts start after 21 days of clean readings.',
        fit_failed: 'The readings don’t follow the weather closely enough to fit a forecast yet.',
        no_weather_forecast: 'No weather forecast was available for this location.',
      },
      lagging: 'This sensor’s readings are behind: last full day {date}. Days up to today are estimated.',
      estimated: '(estimated)',
      deficit: 'At the driest point of the outlook, moisture is about {pts} pts below its usual wet level ({usual}%).',
      water7: 'Next 7 days: {rain} rain vs {et} ET₀.',
      risesWithoutRain: 'Moisture rose without rain on {n} of the last 90 days.',
      belowOnDry: 'If not irrigated, it reaches your minimum ({min}%) around {date}.',
      staysAboveDry: 'Stays above your minimum ({min}%) for 14 days even without irrigation.',
      irrDetected: 'Regular watering detected: {n} of the last {window} days, mostly from {from}.',
      irrPerson: 'Irrigation confirmed by {name} on {date}.',
      irrPersonNoName: 'Irrigation confirmed for this sensor.',
      irrSchedule: 'Irrigation from the facility schedule.',
      battLow: 'Battery trending down: about {days} days to low.',
    },
  },
  es: {
    sf: {
      tab: 'Pronóstico',
      measured: 'Medido (promedio diario)',
      forecast: 'Pronóstico (promedio diario)',
      range80: 'Rango probable (80% de probabilidad)',
      range50: 'Lo más probable (50% de probabilidad)',
      ifNotIrrigated: 'Sin riego',
      daily: 'Diario',
      hourly: 'Por hora',
      measuredHourly: 'Medido (por hora)',
      forecastHourly: 'Pronóstico (por hora)',
      hourlyNote: 'Hora por hora para los próximos 3 días. Parte de la última lectura y sigue el patrón habitual de día y noche de este sensor.',
      showHours: 'Ver horas',
      forecastRegion: 'Pronóstico',
      horizon: { 3: 'Próximos 3 días', 7: 'Próximos 7 días', 14: 'Próximos 14 días' },
      skill: {
        3: 'Los días 1–3 son la parte más confiable del pronóstico.',
        7: 'Los días 4–7 sirven para planificar; el rango se amplía cada día.',
        14: 'Los días 8–14 solo muestran la tendencia; dependen de pronósticos de lluvia que cambian a menudo.',
      },
      noData: 'No hay pronóstico para mostrar.',
      loading: 'Cargando…',
      table: {
        day: 'Día',
        expected: 'Esperado',
        range: 'Rango probable',
        dry: 'Sin riego',
        rain: 'Lluvia (probabilidad)',
        et0: 'ET₀',
      },
      weatherTitle: 'Lluvia y evapotranspiración (ET₀) diarias',
      rain: 'Lluvia',
      et0: 'ET₀',
      uncertaintyTitle: '¿Qué tan seguro es este pronóstico?',
      bandLive: 'El rango sombreado viene de {n} pronósticos de este sensor que después se compararon con sus lecturas.',
      bandHindcast: 'El rango sombreado viene de cuánto falló el modelo en este sensor en los últimos 30 días. Cambia a pronósticos comprobados cuando haya suficientes.',
      bandNone: 'Aún no hay rango probable para esta lectura; tome la línea como guía.',
      trackRecord: 'Pronósticos a 3 días comprobados: {n}. Error medio {fc} pts, vs {ps} pts para “igual que hoy”.',
      trackPending: 'Aún no hay pronósticos comprobados. El historial de aciertos aparece aquí cuando venzan los primeros (unos 3 días).',
      outlookTitle: 'Perspectiva',
      mlTitle: 'Pronóstico con aprendizaje automático',
      mlExplain: 'Se ajusta en cada ejecución a las lecturas de este sensor y al clima local. Cada pronóstico se compara después con lo que el sensor midió realmente, y esas comprobaciones corrigen el siguiente.',
      updated: 'Actualizado {when}',
      tierLabel: 'Confiabilidad',
      tier: {
        insufficient: 'Datos insuficientes',
        learning: 'Aprendiendo',
        good: 'Buena',
        mature: 'Madura',
      },
      tierScale: 'Aprendiendo desde 21 días de lecturas limpias · Buena desde 45 · Madura desde 60.',
      cleanDays: '{n} días de lecturas limpias en los últimos 90',
      tierNeed: 'Faltan {days} días de lecturas limpias para llegar a “{tier}”.',
      tierMature: 'Historial suficiente para los pronósticos más confiables.',
      reasonTitle: 'Sin pronóstico para este sensor por ahora',
      reason: {
        no_data: 'El sensor no tiene lecturas recientes.',
        stale: 'El sensor dejó de reportar. Los pronósticos se reanudan cuando vuelvan las lecturas.',
        out_of_ground: 'Las lecturas indican que la sonda está fuera del suelo (humedad casi cero, sin salinidad).',
        insufficient_history: 'Aún no hay suficiente historial. Los pronósticos empiezan tras 21 días de lecturas limpias.',
        fit_failed: 'Las lecturas aún no siguen el clima lo suficiente para ajustar un pronóstico.',
        no_weather_forecast: 'No hubo pronóstico del clima para esta ubicación.',
      },
      lagging: 'Las lecturas de este sensor están atrasadas: último día completo {date}. Los días hasta hoy son estimados.',
      estimated: '(estimado)',
      deficit: 'En el punto más seco de la perspectiva, la humedad queda unos {pts} pts por debajo de su nivel húmedo habitual ({usual}%).',
      water7: 'Próximos 7 días: {rain} de lluvia vs {et} de ET₀.',
      risesWithoutRain: 'La humedad subió sin lluvia en {n} de los últimos 90 días.',
      belowOnDry: 'Sin riego, llega a su mínimo ({min}%) alrededor del {date}.',
      staysAboveDry: 'Se mantiene sobre su mínimo ({min}%) 14 días incluso sin riego.',
      irrDetected: 'Riego regular detectado: {n} de los últimos {window} días, sobre todo desde las {from}.',
      irrPerson: 'Riego confirmado por {name} el {date}.',
      irrPersonNoName: 'Riego confirmado para este sensor.',
      irrSchedule: 'Riego según el calendario de la instalación.',
      battLow: 'La batería va en descenso: unos {days} días hasta nivel bajo.',
    },
  },
};
